
<template>
  <div class="cfa-root">
    <cfaNav>
      <template v-slot:title>
        <h1 class="h2 mb-0">Créer une campagne de simulation</h1>
      </template>
      <template v-slot:content>
        <div class="container-fluid p-4">
          <div class="card mx-auto" style="max-width: 1024px">
            <div class="card-body p-4">
              <!-- Title changes based on current step -->
              <h1 class="h3 mb-4">
                <template v-if="activeIndex === 0 && !showSuccess">Template</template>
                <template v-if="activeIndex === 1 && !showSuccess">Initialisation</template>
                <template v-if="activeIndex === 2 && !showSuccess">Choix de la cible</template>
                <template v-if="activeIndex === 3 && !showSuccess">Planification</template>
                <template v-if="activeIndex === 4 && !showSuccess">Récapitulatif</template>
                <template v-if="showSuccess"></template>
              </h1>

              <!-- Progress steps - only show if not on success screen -->
              <div v-if="!showSuccess" class="mb-4">
                <div class="position-relative">
                  <!-- Progress bar -->
                  <div class="progress" style="height: 4px">
                    <div class="progress-bar" role="progressbar" :style="{ width: progressWidth }" aria-valuemin="0" aria-valuemax="100"></div>
                  </div>

                  <!-- Step circles -->
                  <div class="d-flex justify-content-between position-relative" style="margin-top: -12px">
                    <div v-for="(step, i) in steps" :key="i"
                        class="rounded-circle d-flex align-items-center justify-content-center bg-white"
                        :class="[i <= activeIndex ? 'btn-primary' : 'border']"
                        style="width: 24px; height: 24px">
                      <i v-if="i < activeIndex" class="bi bi-check-lg"></i>
                      <span v-else>{{ i + 1 }}</span>
                    </div>
                  </div>
                </div>

                <!-- Step labels -->
                <div class="d-flex justify-content-between mt-2">
                  <div v-for="(step, i) in steps" :key="i" class="text-center small"
                      :class="[i <= activeIndex ? 'text-primary fw-bold' : 'text-muted']">
                    {{ step.label }}
                  </div>
                </div>
              </div>

              <!-- Success screen -->
              <div v-if="showSuccess" class="text-center py-5">
                <div class="mb-4">
                  <div class="d-inline-block position-relative">
                    <div class="rounded-circle bg-primary d-flex align-items-center justify-content-center" style="width: 96px; height: 96px">
                      <i class="bi bi-check-lg text-white display-5"></i>
                    </div>
                  </div>
                </div>
                <h2 class="h4 mb-4">Votre campagne de sensibilisation a été effectué avec succès.</h2>
                <button class="btn btn-primary btn-lg" @click="goToList">Retour</button>
              </div>

              <!-- Step content - only show if not on success screen -->
              <div v-else class="step-content">
                <!-- Template selection step -->
                <div v-if="activeIndex === 0">
                  <h2 class="h5 mb-4">Sélectionnez votre template pour la campagne</h2>

                  <div class="row g-4">
                    <div v-for="(template, index) in templates" :key="index" class="col-md-4">
                      <div class="card h-100" :class="{'border-primary': selectedTemplate === index}"
                          style="cursor: pointer" @click="selectTemplate(index)">
                        <img :src="template.image" :alt="template.name" class="card-img-top" style="height: 200px; object-fit: cover">
                        <div class="card-body">
                          <h5 class="card-title" :class="{'text-primary': selectedTemplate === index}">
                            {{ template.name }}
                          </h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Initialisation step -->
                <div v-if="activeIndex === 1">
                  <div class="mb-4">
                    <div class="d-flex mb-4">
                      <img :src="selectedTemplateImage" alt="Template sélectionné" class="rounded me-3" style="width: 128px; height: 128px; object-fit: cover">
                      <div>
                        <h3 class="h5">{{ selectedTemplateName }}</h3>
                      </div>
                    </div>

                    <div class="mb-4">
                      <h3 class="h5 mb-3">Type de campagne</h3>
                      <div class="d-flex gap-4">
                        <div class="form-check">
                          <input type="radio" v-model="campaign.type" value="phishing" id="phishing" class="form-check-input">
                          <label class="form-check-label" for="phishing">Phishing</label>
                        </div>
                        <div class="form-check">
                          <input type="radio" v-model="campaign.type" value="smishing" id="smishing" class="form-check-input">
                          <label class="form-check-label" for="smishing">Smishing</label>
                        </div>
                      </div>
                      <p class="form-text mt-2">
                        {{ campaign.type === 'phishing' 
                          ? 'Dans ce mode il sera envoyer aux utilisateurs des mails contenants des liens malveillants.'
                          : 'Dans ce mode il sera envoyer aux utilisateurs des sms contenants des liens malveillants.' }}
                      </p>
                    </div>

                    <div class="mb-3">
                      <label for="campaign-name" class="form-label">Entrez le nom de votre campagne</label>
                      <input type="text" id="campaign-name" v-model="campaign.name" class="form-control" placeholder="Nom campagne">
                    </div>

                    <div class="alert alert-warning">
                      <i class="bi bi-info-circle me-2"></i>
                      Attribuez un nom unique à votre campagne d'audit dans le champ ci-dessus
                    </div>
                  </div>
                </div>

                <!-- Target selection step -->
                <div v-if="activeIndex === 2">
                  <div class="mb-4">
                    <div class="d-flex mb-4">
                      <img :src="selectedTemplateImage" alt="Template sélectionné" class="rounded me-3" style="width: 128px; height: 128px; object-fit: cover">
                      <div>
                        <h3 class="h5">{{ selectedTemplateName }}</h3>
                      </div>
                    </div>

                    <div class="mb-4">
                      <h3 class="h5 mb-3">Cible de votre campagne</h3>
                      <div class="d-flex gap-4">
                        <div class="form-check">
                          <input type="radio" v-model="campaign.targetType" value="group" id="group-target" class="form-check-input">
                          <label class="form-check-label" for="group-target">Groupe cible</label>
                        </div>
                        <div class="form-check">
                          <input type="radio" v-model="campaign.targetType" value="service" id="service-target" class="form-check-input">
                          <label class="form-check-label" for="service-target">Service cible</label>
                        </div>
                      </div>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">{{ campaign.targetType === 'group' ? 'Groupe cible' : 'Service cible' }}</label>
                      <select v-model="campaign.selectedTarget" class="form-select">
                        <option value="">{{ campaign.targetType === 'group' ? 'Sélectionner un groupe' : 'Sélectionner un service' }}</option>
                        <option v-for="target in availableTargets" :key="target.id" :value="target">
                          {{ target.name }}
                        </option>
                      </select>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Les utilisateurs de ce {{ campaign.targetType === 'group' ? 'groupe' : 'service' }}</label>
                      <div v-if="isLoadingUsers" class="text-center py-3">
                        <div class="spinner-border text-primary"></div>
                      </div>
                      <div v-else class="border rounded p-3" style="max-height: 200px; overflow-y: auto">
                        <div v-if="groupUsers.length === 0" class="text-center text-muted py-2">
                          Aucun utilisateur trouvé
                        </div>
                        <div v-for="(user, index) in groupUsers" :key="user.id" class="py-2 border-bottom">
                          <div class="d-flex align-items-center">
                            <span class="me-2">{{ index + 1 }}.</span>
                            <span>{{ user.name }}</span>
                            <span v-if="user.email" class="ms-2 small text-muted">({{ user.email }})</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="alert alert-warning">
                      <i class="bi bi-info-circle me-2"></i>
                      Seuls les membres du {{ campaign.targetType === 'group' ? 'groupe' : 'service' }} cible recevront la campagne.
                      Vous pouvez modifier cette liste dans le menu de gestion des utilisateurs.
                    </div>
                  </div>
                </div>

                <!-- Planning step -->
                <div v-if="activeIndex === 3">
                  <div class="mb-4">
                    <div class="d-flex mb-4">
                      <img :src="selectedTemplateImage" alt="Template sélectionné" class="rounded me-3" style="width: 128px; height: 128px; object-fit: cover">
                      <div>
                        <h3 class="h5">{{ selectedTemplateName }}</h3>
                      </div>
                    </div>

                    <div class="mb-4">
                      <div class="mb-3">
                        <label class="form-label text-danger">
                          Sélectionnez date et heure de début (un délai d'attente de 03 heures sera ajouté à l'heure de lancement choisie)
                        </label>
                        <input type="datetime-local" v-model="campaign.startDate" class="form-control w-50">
                      </div>

                      <div class="mb-3">
                        <label class="form-label text-danger">
                          Sélectionnez date et heure de fin (un minimum d'une journée après le début de la campagne)
                        </label>
                        <input type="datetime-local" v-model="campaign.endDate" class="form-control w-50" :min="minEndDate">
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Summary step -->
                <div v-if="activeIndex === 4">
                  <div class="mb-4">
                    <div class="d-flex mb-4">
                      <img :src="selectedTemplateImage" alt="Template sélectionné" class="rounded me-3" style="width: 128px; height: 128px; object-fit: cover">
                      <div>
                        <h3 class="h5">{{ selectedTemplateName }}</h3>
                      </div>
                    </div>

                    <div class="mb-4">
                      <h3 class="h5 mb-2">Type de campagne</h3>
                      <div class="d-flex align-items-center mb-3">
                        <i class="bi bi-circle-fill text-primary me-2"></i>
                        <span>{{ campaign.type === 'phishing' ? 'Phishing' : 'Smishing' }}</span>
                      </div>
                      <p v-if="campaign.type === 'phishing'" class="mb-3 text-muted small">
                        Dans ce mode il sera envoyer aux utilisateurs des mails contenants des liens malveillants.
                      </p>

                      <h3 class="h5 mb-2">Nom campagne</h3>
                      <input type="text" v-model="campaign.name" class="form-control mb-3" readonly disabled>

                      <h3 class="h5 mb-2">Cible de votre campagne</h3>
                      <div class="d-flex align-items-center mb-3">
                        <i class="bi bi-circle-fill text-primary me-2"></i>
                        <span>{{ campaign.targetType === 'group' ? 'Groupe cible' : 'Service cible' }}</span>
                      </div>

                      <select v-model="campaign.selectedTarget" class="form-select mb-3" disabled>
                        <option v-for="target in availableTargets" :key="target.id" :value="target">
                          {{ target.name }}
                        </option>
                      </select>

                      <div class="mb-3">
                        <label class="form-label">Les utilisateurs de ce {{ campaign.targetType === 'group' ? 'groupe' : 'service' }}</label>
                        <div class="border rounded p-3" style="max-height: 200px; overflow-y: auto">
                          <div v-if="groupUsers.length === 0" class="text-center text-muted py-2">
                            Aucun utilisateur trouvé
                          </div>
                          <div v-for="(user, index) in groupUsers" :key="user.id" class="py-2 border-bottom">
                            <div class="d-flex align-items-center">
                              <span class="me-2">{{ index + 1 }}.</span>
                              <span>{{ user.name }}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Date et heure de début de la campagne</label>
                        <input type="datetime-local" v-model="campaign.startDate" class="form-control" disabled>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Date et heure de fin de la campagne</label>
                        <input type="datetime-local" v-model="campaign.endDate" class="form-control" disabled>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Navigation buttons - only show if not on success screen -->
              <div v-if="!showSuccess" class="d-flex mt-4" :class="[activeIndex > 0 ? 'justify-content-between' : 'justify-content-center']">
                <button class="btn btn-primary btn-lg" @click="prevStep" :disabled="activeIndex === 0" v-show="activeIndex > 0">
                  Précédent
                </button>

                <button v-if="activeIndex < 4" class="btn btn-primary btn-lg" @click="nextStep" :disabled="!canContinue">
                  Continuer
                </button>

                <button v-else class="btn btn-primary btn-lg" @click="submitCampaign" :disabled="!canContinue || isSubmitting">
                  <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                  {{ isSubmitting ? 'Traitement en cours...' : 'Valider' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </cfaNav>
  </div>
</template>
  
  <script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import cfaNav from '@/components/nav/CFANav.vue';
  import { fetchPhishingTemplates, createCampaign } from '@/services/campaignService';
  import { fetchGroups, fetchGroupById } from '@/services/groupService';
  import { fetchServices, fetchServiceUsers } from '@/services/serviceService';
  
  const router = useRouter();
  
  // Steps configuration
  const steps = ref([
    { label: 'Template' },
    { label: 'Initialisation' },
    { label: 'Choix de la cible' },
    { label: 'Planification' },
    { label: 'Récapitulatif' }
  ]);
  
  const activeIndex = ref(0);
  const showSuccess = ref(false);
  
  // Progress bar width calculation
  const progressWidth = computed(() => {
    const totalSteps = steps.value.length - 1;
    const progress = (activeIndex.value / totalSteps) * 100;
    return `${progress}%`;
  });
  
  // Template data
  const templates = ref([]);
  
  // Fetch templates from service
  const fetchTemplates = async () => {
    try {
      const fetchedTemplates = await fetchPhishingTemplates();
      if (fetchedTemplates.length > 0) {
        templates.value = fetchedTemplates;
      } else {
        // Fallback to default templates if API returns empty
        templates.value = [
          { 
            name: 'Nom template', 
            image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?q=80&w=1974&auto=format&fit=crop' 
          },
          { 
            name: 'Nom template', 
            image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?q=80&w=1974&auto=format&fit=crop' 
          }
        ];
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Fallback templates in case of error
      templates.value = [
        { 
          name: 'Nom template', 
          image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?q=80&w=1974&auto=format&fit=crop' 
        },
        { 
          name: 'Nom template', 
          image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?q=80&w=1974&auto=format&fit=crop' 
        }
      ];
    }
  };
  
  // Groups and services data
  const groups = ref([]);
  const services = ref([]);
  const isLoading = ref(false);
  
  // Fetch groups and services
  const fetchTargetData = async () => {
    isLoading.value = true;
    try {
      // Fetch groups
      const fetchedGroups = await fetchGroups();
      groups.value = fetchedGroups.map(group => ({
        id: group.id,
        name: group.name,
        userCount: group.users?.length || 0
      }));
      
      // Fetch services
      const fetchedServices = await fetchServices();
      services.value = fetchedServices.map(service => ({
        id: service.id,
        name: service.name
      }));
    } catch (error) {
      console.error('Error fetching groups and services:', error);
    } finally {
      isLoading.value = false;
    }
  };
  
  // Users data
  const groupUsers = ref([]);
  const isLoadingUsers = ref(false);
  
  // Fetch users when a group or service is selected
  const fetchUsers = async () => {
    if (!campaign.value.selectedTarget) return;
    
    isLoadingUsers.value = true;
    groupUsers.value = [];
    
    try {
      if (campaign.value.targetType === 'group') {
        
        // Fetch group users
        const groupData = await fetchGroupById(campaign.value.selectedTarget.id);
        if (groupData && groupData.user_detail) {
          groupUsers.value = groupData.user_detail.map(user => ({
            id: user.id,
            name: `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'Utilisateur sans nom',
            email: user.email
          }));
        }
      } else {
        // Fetch service users
        const serviceUsers = await fetchServiceUsers(campaign.value.selectedTarget.id);
        if (serviceUsers) {
          groupUsers.value = serviceUsers.map(user => ({
            id: user.id,
            name: `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'Utilisateur sans nom',
            email: user.email
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching users for ${campaign.value.targetType}:`, error);
    } finally {
      isLoadingUsers.value = false;
    }
  };
  
  // Call fetchTemplates and fetchTargetData when component is mounted
  onMounted(() => {
    fetchTemplates();
    fetchTargetData();
  });
  
  const selectedTemplate = ref(null);
  const selectedTemplateName = computed(() => {
    return selectedTemplate.value !== null ? templates.value[selectedTemplate.value].name : '';
  });
  const selectedTemplateImage = computed(() => {
    return selectedTemplate.value !== null ? templates.value[selectedTemplate.value].image : '';
  });
  
  // Campaign data
  const campaign = ref({
    name: '',
    type: 'phishing',
    targetType: 'group',
    selectedTarget: null,
    startDate: null,
    endDate: null
  });
  
  // Watch for changes in selectedTarget to fetch users
  watch(() => campaign.value.selectedTarget, (newValue) => {
    if (newValue) {
      fetchUsers();
    } else {
      groupUsers.value = [];
    }
  });
  
  // Watch for changes in targetType to reset selectedTarget
  watch(() => campaign.value.targetType, () => {
    campaign.value.selectedTarget = null;
    groupUsers.value = [];
  });
  
  // Min end date (1 day after start date)
  const minEndDate = computed(() => {
    if (!campaign.value.startDate) return null;
    
    const date = new Date(campaign.value.startDate);
    date.setDate(date.getDate() + 1);
    return date;
  });
  
  // Computed properties
  const canContinue = computed(() => {
    if (activeIndex.value === 0) {
      return selectedTemplate.value !== null;
    } else if (activeIndex.value === 1) {
      return campaign.value.name && campaign.value.type;
    } else if (activeIndex.value === 2) {
      return campaign.value.targetType && campaign.value.selectedTarget;
    } else if (activeIndex.value === 3) {
      return campaign.value.startDate && campaign.value.endDate;
    }
    return true; // Summary step can always continue
  });
  
  // Get available targets based on targetType
  const availableTargets = computed(() => {
    return campaign.value.targetType === 'group' ? groups.value : services.value;
  });
  
  // Methods
  const selectTemplate = (index) => {
    selectedTemplate.value = index;
  };
  
  const nextStep = () => {
    if (activeIndex.value < steps.value.length - 1) {
      activeIndex.value++;
    }
  };
  
  const prevStep = () => {
    if (activeIndex.value > 0) {
      activeIndex.value--;
    }
  };
  const isSubmitting = ref(false);
  
  const submitCampaign = async () => {
    try {
      // Indique que la soumission est en cours
      isSubmitting.value = true;
      
      // Récupère le template sélectionné
      const template = templates.value[selectedTemplate.value];
      
      // Prépare les données selon le type de cible (groupe ou service)
      const groups = [];
      let service = '';
      
      if (campaign.value.targetType === 'group') {
        groups.push(campaign.value.selectedTarget.id);
      } else {
        service = campaign.value.selectedTarget.id;
      }
      
      // Ajoute 3 heures à la date de début (comme dans le code original)
      const startDate = new Date(campaign.value.startDate);
      startDate.setHours(startDate.getHours() + 3);
      
      // Prépare le payload selon le type de campagne
      const payload = {
        name: campaign.value.name,
        gophish_landing_page_id: template.landing_page_id,
        gophish_template_id: template.template_id,
        smtp: "",
        url: "",
        organisation: localStorage.organisation,
      };
      
      // Ajoute les champs spécifiques selon le type de cible
      if (campaign.value.targetType === 'group') {
        payload.groups = groups;
      } else {
        payload.service = service;
      }
      
      // Ajoute les dates avec les noms de champs appropriés selon le type de campagne
      if (campaign.value.type === 'phishing') {
        payload.launch_date = startDate;
        payload.end_date = campaign.value.endDate;
      } else {
        payload.launch_date = startDate;
        payload.closing_date = campaign.value.endDate;
      }
      
      // Appelle l'API pour créer la campagne
      await createCampaign({
        type: campaign.value.type,
        ...payload
      });
      
      // Affiche l'écran de succès
      showSuccess.value = true;
      isSubmitting.value = false;
      
    } catch (error) {
      console.error('Error submitting campaign:', error);
      // Gestion des erreurs (vous pourriez ajouter un toast ou une notification ici)
      isSubmitting.value = false;
    }
  };
  
  const goToList = () => {
    // Navigate back to campaign list
    router.push('/audits');
  };
  </script>
  
  <style lang="scss" scoped>
@use "../../assets/scss/index.scss" as *;
  /*
  :deep(.p-button) {
    border-radius: 0;
  }
  
  :deep(.p-inputtext),
  :deep(.p-dropdown) {
    border-radius: 0;
  }
  
  .bg-navy-800 {
    background-color: #132c4e;
  }
  
  .bg-cyan-500 {
    background-color: #0cbcc6;
  }
  
  .text-cyan-500 {
    color: #0cbcc6;
  }
  
  .border-cyan-500 {
    border-color: #0cbcc6;
  }
  */
  .step-content {
    min-height: 400px;
  }
  </style>